import { useMemo } from 'react';

import useTranslation from '~/app/hooks/useTranslation';

import { getStatuses, StatusWithLabel } from './common';
import {TypesWithLabel} from "~/app/data/statuses/common";
import { CostCategory, Customer, HPCode, IssueSortingType, Project, WorkType } from '~/types';

export enum IssueTypes {
    MINOR = 0,
    CRITICAL = 1,
    MORECRITICAL = 2
}

export const IssueTypeRepaired = 3;

export enum SOURCE_ISSUE_TYPE {
    DEPARTMENT_REMAINING_WORK = 1,
    MACHINE_CARD = 2,
    DAILY_CHECK = 3,
    FELT_APP = 4,
    WORKORDER = 5,
    SERVICE_CHECKLIST = 6,
    SERVICE_CHECKLIST_FELT_APP = 7,
}

export enum IssueStatuses {
    REGISTERED = 0,
    INPROGRESS = 1,
    COMPLETED = 2,
    CLOSED = 3
}

type IssueStatusesData = StatusWithLabel<IssueStatuses>[]

interface UseIssueStatuses {
    (): IssueStatusesData
}

export const useIssueStatuses: UseIssueStatuses = () => {
    const { t } = useTranslation('issue.statuses');

    return useMemo<IssueStatusesData>(() => (
        getStatuses(IssueStatuses).map(key => ({ label: t(key), value: +key }))
    ), [t]);
};

export const useIssueTypes = (): TypesWithLabel<IssueTypes>[] => {
    const { t } = useTranslation('issue.types');

    return useMemo(() => (
        getStatuses(IssueTypes).map(key => ({ text: t(key), value: +key }))
    ), [t]);
};

export const DEFAULT_EDIT_COLOR = '#FF453A';

export const COLOR_OPTIONS  = ["#FFFFFF", "#000000", "#0A84FF", "#30D158", "#FFD60A", DEFAULT_EDIT_COLOR];

export const EDIT_IMAGE_DEFAULT_EXTENSION = "png";

export const EDIT_IMAGE_DEFAULT_MIME_TYPE = "image/png";

export const EDIT_IMAGE_EXPORT_QUALITY = 1.0;

export const EDIT_IMAGE_RESOLUTION_MULTIPLIER = 3;

export enum IssueCloseTypes {
    CORRECTED = '2',
    NOTWRONG = '1',
    CORRECTEDWORKORDER = '3'
}

export enum MaterialTypes {
    AUTO = 1,
    MANUAL = 2
}

export enum MaterialLinkTypes {
    ISSUE = 0,
    SERVICE = 1
}

export const DEFAULT_ISSUE_CLOSE_TYPE = IssueCloseTypes.CORRECTED;

export const MAX_ISSUE_IMAGES = 5;
export const MAX_ISSUE_IMAGE_SIZE_MB = 10;
export const MAX_ISSUE_FILES = 5;
export const MAX_ISSUE_FILE_SIZE_MB = 50;

export const useIssueCloseOptions = (showSimplifiedWorkOrder?: boolean) => {
    const { t } = useTranslation('issue.issueClose');

    return useMemo(() => {
        const options = [
            { label: t('corrected'), value: IssueCloseTypes.CORRECTED },
            { label: t('notWrong'), value: IssueCloseTypes.NOTWRONG },
        ];

        if (showSimplifiedWorkOrder) {
            options.push({ label: t('correctedWorkOrder'), value: IssueCloseTypes.CORRECTEDWORKORDER });
        }

        return options;
    }, [t, showSimplifiedWorkOrder]);
};

export const useIssueSortingTypes = (sortingTypes: IssueSortingType[]): TypesWithLabel<IssueSortingType['id']>[] => {
    return useMemo(() => (
        sortingTypes ? sortingTypes?.map(sortingType => ({ text: sortingType.name, value: sortingType.id })) : []
    ), [sortingTypes]);
};

export const selectedSortingType = (sortingTypes: IssueSortingType[],  sortingTypeId: string | number) => {
    return sortingTypes?.find(sortingType => Number(sortingType.id) === Number(sortingTypeId))?.name || '';
}

export const useSelectedIssueSortingType = (sortingTypes: IssueSortingType[],  sortingTypeId: string | number): IssueSortingType['name'] => {
    return useMemo(() => selectedSortingType(sortingTypes, sortingTypeId), [sortingTypeId, sortingTypes]);
};

export const useWorkOrderHPCodes = (hpCodes: HPCode[]): TypesWithLabel<HPCode['id']>[] => {
    return useMemo(() => (
        hpCodes ? hpCodes?.map(hpCode => ({ text: `${hpCode.code} - ${hpCode.name}`, value: hpCode.id })) : []
    ), [hpCodes]);
};

export const useWorkTypes = (workTypes: WorkType[]): TypesWithLabel<WorkType['id']>[] => {
    return useMemo(() => (
        workTypes ? workTypes?.map(workType => ({ text: workType.name, value: workType.id })) : []
    ), [workTypes]);
};

export const useCostCategories = (costCategories: CostCategory[]): TypesWithLabel<CostCategory['id']>[] => {
    return useMemo(() => (
        costCategories ? costCategories?.map(costCategory => ({ text: `${costCategory.name} - ${costCategory.description}`, value: costCategory.id })) : []
    ), [costCategories]);
};

export const useCustomers = (customers: Customer[]): TypesWithLabel<Customer['id']>[] => {
    return useMemo(() => (
        customers ? customers?.map(customer => ({ text: `${customer.customerNumber} - ${customer.customerName}`, value: customer.id })) : []
    ), [customers]);
};

export const useProjects = (projects: Project[]): TypesWithLabel<Project['id']>[] => {
    return useMemo(() => (
        projects ? projects?.map(project => ({ text: `${project.number} - ${project.name}`, value: project.id })) : []
    ), [projects]);
};